import { ModalSimple } from "@/components/modal/ModalSimple.tsx";
import { Row } from "@/components/common/row.ts";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { IconX } from "@/icons/IconX.tsx";
import { t } from "@/themes/theme.ts";
import { IconTick } from "@/icons/IconTick.tsx";
import { styled } from "@linaria/react";
import { useMutation } from "@tanstack/react-query";
import { postRequestHelp, postSlackMessage } from "@/api/misc.ts";
import { toastAndError } from "@/helpers/toast.ts";
import { toast } from "sonner";
import { useUserStore } from "@/stores/useUserStore.ts";
import { PRODUCTION } from "@/constants/settings.ts";
import red_alert from "@/assets/audio/red_alert.mp3";
import type { FC } from "react";

const AudioRedAlert = new Audio(red_alert);

const AlertText = styled.p`
  padding: 1rem 0;
`;

const ExtraSlackAddition = !PRODUCTION ? " (on QA)" : "";

interface ModalAlertProps {
  onClose: () => void;
}

export const ModalAlert: FC<ModalAlertProps> = ({ onClose }) => {
  const { userDetails } = useUserStore();

  const redAlert = useMutation({
    mutationFn: () =>
      Promise.all([
        postSlackMessage(
          `:rotating_light: Tutor *${userDetails?.userName}* has requested assistance on the bot${ExtraSlackAddition} :rotating_light:`,
        ),
        postRequestHelp(),
      ]),
    onError: (error) => {
      toastAndError("Could not send request for help, API may be down?", error);
    },
    onSuccess: () => {
      AudioRedAlert.play().catch((e) => console.error("Could not play audio", e));
      toast.info("Request for help has been sent!");
    },
  });

  return (
    <ModalSimple title={"Request help?"} onClick={() => onClose()}>
      <AlertText>
        Would you like to request assistance?
        <br />
        Make sure you've tried a hard refresh just in-case!
      </AlertText>
      <Row data-justify={"between"}>
        <ButtonIcon
          icon={<IconX width={18} height={18} />}
          text={"I've changed my mind"}
          onClick={() => onClose()}
          backgroundColor={t.brandNeutralBlack}
          flare
        />
        <ButtonIcon
          icon={<IconTick width={18} height={18} />}
          text={"Help!"}
          onClick={() => {
            onClose();
            redAlert.mutate();
          }}
          backgroundColor={t.brandAccentRed}
          flare
        />
      </Row>
    </ModalSimple>
  );
};
